import React, { useState } from 'react';
import { Text, Tooltip } from '@fragments';
import { IconArrowUpRight, IconArrowsHorizontal, IconChartBar, IconInfoCircle, IconMovie, IconTrendingDown, IconTrendingUp } from '@tabler/icons-react';
import { compactCount } from '../../../../components/AccountSummary';
import './styles.scss';

const TIERS = [['top', IconTrendingUp], ['middle', IconArrowsHorizontal], ['bottom', IconTrendingDown]];
const COMPARED = ['ready', 'no_pattern'];

// "12,3 mil visualizações", or "12,3 mil curtidas + comentários" when Meta gave no views.
export const reachLabel = (t, locale, metric, value) => {
  const shown = compactCount(value, locale);
  return metric === 'engagement' ? t('personalityEngagement', { value: shown }) : t('personalityViews', { count: value, value: shown });
};
// A caption keeps to one line: its own line breaks would otherwise turn on `pre-line`.
const oneLine = caption => (caption || '').replace(/\s+/g, ' ').trim();

// The Studio's copy of a reel's cover; the film glyph when there is none or it expired.
function Cover({ video }) {
  const [broken, setBroken] = useState(false);
  const url = !broken && video.preview?.url;
  return <span className="studio-personality-works__cover">
    {url ? <img src={url} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} /> : <IconMovie aria-hidden="true" />}
  </span>;
}

// The reel on Instagram, in a new tab; without a permalink it is shown, not linked (its text reads as is).
function ReelLink({ video, className, label, children }) {
  return video.permalink
    ? <a className={className} href={video.permalink} target="_blank" rel="noopener noreferrer" aria-label={label}>{children}</a>
    : <span className={className}>{children}</span>;
}

// The best reels a pattern was seen in, as small covers. The Tooltip anchors to a DOM element,
// so the link is written out here rather than through ReelLink.
function Evidence({ t, locale, metric, videos }) {
  if (!videos.length) return null;
  return <ul className="studio-personality-works__evidence" aria-label={t('personalityWorksEvidence')}>
    {videos.map(video => {
      const label = `${oneLine(video.caption) || t('personalityUntitled')} · ${reachLabel(t, locale, metric, video.value)}`;
      return <li key={video.video_id}>
        <Tooltip content={label} placement="top">
          {video.permalink
            ? <a className="studio-personality-works__thumb" href={video.permalink} target="_blank" rel="noopener noreferrer" aria-label={label}><Cover video={video} /></a>
            : <span className="studio-personality-works__thumb" role="img" aria-label={label}><Cover video={video} /></span>}
        </Tooltip>
      </li>;
    })}
  </ul>;
}

function Point({ label, contrast, children }) {
  return <div className="studio-personality-works__point">
    <Text.caption contrast={6}>{label}</Text.caption>
    <Text.span contrast={contrast}>{children}</Text.span>
  </div>;
}

// One compared reel: cover, caption and reach; a reel that could not be transcribed stays
// listed, a step back, as left out of the comparison.
function ReelRow({ t, locale, metric, video }) {
  const caption = oneLine(video.caption) || t('personalityUntitled');
  const out = video.status === 'failed';
  const value = out ? t('personalityComparedOut') : reachLabel(t, locale, metric, video.value);
  return <ReelLink video={video} className={`studio-personality-works__reel${out ? ' is-out' : ''}`}
    label={[caption, value, video.permalink && t('personalityOpenReel')].filter(Boolean).join(' · ')}>
    <Cover video={video} />
    <span className="studio-personality-works__reel-text">
      <Text.span className="studio-personality-works__reel-caption" contrast={8}>{caption}</Text.span>
      <Text.caption contrast={6}>{value}</Text.caption>
    </span>
    {video.permalink && <IconArrowUpRight className="studio-personality-works__reel-open" aria-hidden="true" />}
  </ReelLink>;
}

// "O que funciona pra você" (`personality.performance`, contract 2.5.0): the creator's best
// and worst reels by reach, among the latest 100, compared. When the comparison found what
// the best have in common (`ready`): its summary, one row per pattern (in the best reels · in
// the worst · in the next scripts, with the best reels it was seen in), then the compared
// reels by tier. When it found no pattern, its summary; when it could not compare (reach too
// even, too few reels, Meta or the analysis down), a short note that scripts follow the
// structure that goes viral. Nothing for a personality mapped before 2.5.0.
export default function PersonalityWorks({ studio: s, performance }) {
  const { t, locale } = s;
  if (!performance) return null;
  const { status, metric, videos } = performance;
  const byId = Object.fromEntries(videos.map(video => [video.video_id, video]));
  const ready = status === 'ready';
  const note = ready ? null : status === 'no_pattern' ? performance.summary : t(`personalityWorksStatus_${status}`, { defaultValue: t('personalityWorksStatus_unavailable') });
  return <section className="studio-personality-works" aria-labelledby="studio-personality-works">
    <div className="studio-personality-view__head">
      <Text.label id="studio-personality-works" contrast={9} left={<IconChartBar />}>{t('personalityWorks')}</Text.label>
      <Text.caption contrast={6}>{t('personalityWorksHint')}</Text.caption>
    </div>

    {note && <div className="studio-personality-works__note" role="note">
      <IconInfoCircle aria-hidden="true" />
      <Text.span contrast={8}>{note}</Text.span>
    </div>}

    {ready && <>
      {performance.summary && <Text.p contrast={8}>{performance.summary}</Text.p>}
      {performance.patterns.length > 0 && <ol className="studio-personality-works__patterns" aria-label={t('personalityWorksPatterns')}>
        {performance.patterns.map(pattern => <li key={pattern.area}>
          <div className="studio-personality-works__area">
            <Text.label contrast={9}>{t(`personalityArea_${pattern.area}`)}</Text.label>
            <Evidence t={t} locale={locale} metric={metric} videos={pattern.video_ids.map(id => byId[id]).filter(Boolean)} />
          </div>
          <Point label={t('personalityWorksBest')} contrast={8}>{pattern.best}</Point>
          <Point label={t('personalityWorksRest')} contrast={7}>{pattern.rest}</Point>
          <Point label={t('personalityWorksNext')} contrast={9}>{pattern.direction}</Point>
        </li>)}
      </ol>}

      {videos.length > 0 && <section className="studio-personality-works__compared" aria-labelledby="studio-personality-compared">
        <div className="studio-personality-view__head">
          <Text.label id="studio-personality-compared" contrast={9}>{t('personalityCompared')}</Text.label>
          {performance.median != null && <Text.caption contrast={6}>
            {t('personalityComparedHint', { shown: videos.length, ranked: performance.ranked_videos, median: reachLabel(t, locale, metric, performance.median) })}
          </Text.caption>}
        </div>
        <div className="studio-personality-works__tiers">
          {TIERS.map(([tier, Icon]) => {
            const items = videos.filter(video => video.tier === tier);
            return items.length > 0 && <section key={tier} className="studio-personality-works__tier" aria-label={t(`personalityTier_${tier}`)}>
              <Text.subheading contrast={6} left={<Icon />}>{t(`personalityTier_${tier}`)}</Text.subheading>
              <ol className="studio-personality-works__reels">
                {items.map(video => <li key={video.video_id}><ReelRow t={t} locale={locale} metric={metric} video={video} /></li>)}
              </ol>
            </section>;
          })}
        </div>
      </section>}
    </>}

    {COMPARED.includes(status) && performance.limitations && <Text.caption contrast={6}>{performance.limitations}</Text.caption>}
  </section>;
}
